(() => {
  const STORAGE_KEY = "shopify_wishlist_v1";
  const CHANGE_EVENT = "shopify:wishlist:change";

  // ---------- Utility functions (unchanged) ----------
  function safeParse(value, fallback = []) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : fallback;
    } catch {
      return fallback;
    }
  }

  function escapeHtml(str = "") {
    return String(str)
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }

  function readItems() {
    return safeParse(localStorage.getItem(STORAGE_KEY), []);
  }

  function writeItems(items) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
  }

  function getItemId(item) {
    return String(item.id || item.productId || "");
  }

  function normalizeItem(item) {
    return {
      id: String(item.id || ""),
      handle: item.handle || "",
      title: item.title || "",
      url: item.url || "",
      image: item.image || "",
      price: item.price || "",
      variantId: item.variantId || "",
    };
  }

  // ---------- Popup Notification ----------
  const popup = document.getElementById("wishlist-popup");
  let popupTimeout;

  function showPopup(item, action) {
    if (!popup) return;

    const imageEl = popup.querySelector(".wishlist-popup__image");
    const titleEl = popup.querySelector(".wishlist-popup__title");
    const messageEl = popup.querySelector(".wishlist-popup__message");

    if (imageEl) {
      imageEl.src = item.image || "";
      imageEl.alt = item.title || "";
    }
    if (titleEl) titleEl.textContent = item.title || "Product";
    if (messageEl) {
      messageEl.textContent = action === "added"
        ? "Added to wishlist"
        : "Removed from wishlist";
    }

    popup.hidden = false;

    // Clear any existing timeout
    clearTimeout(popupTimeout);
    popupTimeout = setTimeout(() => {
      popup.hidden = true;
    }, 3000);
  }

  // Close button handler (delegated once)
  if (popup) {
    popup.addEventListener("click", (e) => {
      if (e.target.closest(".wishlist-popup__close")) {
        popup.hidden = true;
        clearTimeout(popupTimeout);
      }
    });
  }

  // ---------- Wishlist API ----------
  const Wishlist = {
    getItems() {
      return readItems();
    },

    has(id) {
      const items = readItems();
      return items.some((item) => getItemId(item) === String(id));
    },

    add(item) {
      const items = readItems();
      const normalized = normalizeItem(item);
      if (!normalized.id) return;

      const exists = items.some((current) => getItemId(current) === normalized.id);
      if (exists) return;

      items.push(normalized);
      writeItems(items);
      showPopup(normalized, "added");
    },

    remove(id) {
      const items = readItems();
      const removedItem = items.find((item) => getItemId(item) === String(id));
      const filtered = items.filter((item) => getItemId(item) !== String(id));
      writeItems(filtered);
      if (removedItem) showPopup(removedItem, "removed");
    },

    toggle(item) {
      const id = String(item.id || "");
      if (!id) return;
      if (this.has(id)) {
        this.remove(id);
      } else {
        this.add(item);
      }
    },

    clear() {
      writeItems([]);
    },

    count() {
      return readItems().length;
    },
  };

  // ---------- DOM Helpers ----------
  function buildItemFromButton(button) {
    return {
      id: button.dataset.productId || "",
      handle: button.dataset.productHandle || "",
      title: button.dataset.productTitle || "",
      url: button.dataset.productUrl || "",
      image: button.dataset.productImage || "",
      price: button.dataset.productPrice || "",
      variantId: button.dataset.productVariantId || "",
    };
  }

  function updateButtons(root = document) {
    root.querySelectorAll("[data-wishlist-button]").forEach((button) => {
      const productId = button.dataset.productId;
      const active = Wishlist.has(productId);
      button.classList.toggle("is-active", active);
      button.setAttribute("aria-pressed", active ? "true" : "false");
      button.setAttribute(
        "aria-label",
        active
          ? (button.dataset.wishlistLabelActive || "Remove from wishlist")
          : (button.dataset.wishlistLabel || "Add to wishlist")
      );
      const textEl = button.querySelector("[data-wishlist-text]");
      if (textEl) {
        textEl.textContent = active
          ? (button.dataset.wishlistLabelActive || "Saved")
          : (button.dataset.wishlistLabel || "Wishlist");
      }
    });
  }

  function updateCounts(root = document) {
    const count = Wishlist.count();
    root.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      el.textContent = String(count);
      el.style.display = count > 0 ? "" : "";
    });
  }

  function renderWishlistPage(root = document) {
    const page = root.querySelector("[data-wishlist-page]");
    if (!page) return;
    const grid = page.querySelector("[data-wishlist-grid]");
    const empty = page.querySelector("[data-wishlist-empty]");
    if (!grid) return;

    const items = Wishlist.getItems();
    if (!items.length) {
      grid.innerHTML = "";
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;

    grid.innerHTML = items
      .map((item) => {
        const id = escapeHtml(item.id);
        const title = escapeHtml(item.title);
        const handle = escapeHtml(item.handle);
        const url = item.url ? escapeHtml(item.url) : `/products/${handle}`;
        const image = item.image ? escapeHtml(item.image) : "";
        const price = item.price ? escapeHtml(item.price) : "";
        return `
          <article class="wishlist-item" data-wishlist-item="${id}">
            <a class="wishlist-item__image" href="${url}" aria-label="${title}">
              ${image ? `<img src="${image}" alt="${title}" loading="lazy">` : ""}
            </a>
            <button type="button" class="wishlist-remove" data-wishlist-remove="${id}">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                <path d="M19 5L4.99998 19M5.00001 5L19 19" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="wishlist-item__content">
              <h3 class="wishlist-item__title"><a href="${url}">${title}</a></h3>
              ${price ? `<div class="wishlist-item__price">${price}</div>` : ""}
              <div class="wishlist-item__actions custom-btn">
                <button name="add" class="btn btn-small add-to-cart-btn">
                  <span data-addtocart-text="">View Product</span>
                  <span data-addtocart-arrow="">
                    <svg width="8" height="14" viewBox="0 0 8 14" fill="none">
                      <path d="M0.30592 0.289294C0.11004 0.474585 9.21001e-07 0.72586 9.09208e-07 0.987862C8.97415e-07 1.24986 0.11004 1.50114 0.30592 1.68643L5.47795 6.57739L0.30592 11.4684C0.115591 11.6547 0.0102753 11.9043 0.0126561 12.1634C0.0150365 12.4224 0.124923 12.6703 0.318647 12.8535C0.512371 13.0367 0.774434 13.1406 1.04839 13.1428C1.32235 13.1451 1.58628 13.0455 1.78334 12.8655L7.69408 7.27596C7.88996 7.09067 8 6.8394 8 6.57739C8 6.31539 7.88996 6.06412 7.69408 5.87883L1.78334 0.289294C1.5874 0.104059 1.32169 -2.8348e-07 1.04463 -2.9524e-07C0.767573 -3.07001e-07 0.501859 0.104059 0.30592 0.289294Z" fill="currentColor"/>
                    </svg>
                  </span>
                </button>
              </div>
            </div>
          </article>
        `;
      })
      .join("");
  }

  function refreshUI(root = document) {
    updateButtons(root);
    updateCounts(root);
    renderWishlistPage(root);
  }

  // ---------- Initialisation ----------
  function init() {
    refreshUI();

    document.addEventListener("click", (event) => {
      const addButton = event.target.closest("[data-wishlist-button]");
      if (addButton) {
        event.preventDefault();
        Wishlist.toggle(buildItemFromButton(addButton));
        refreshUI();
        return;
      }

      const removeButton = event.target.closest("[data-wishlist-remove]");
      if (removeButton) {
        event.preventDefault();
        const id = removeButton.dataset.wishlistRemove;
        // Remove triggers its own popup (item data retrieved inside Wishlist.remove)
        Wishlist.remove(id);
        refreshUI();
      }
    });

    window.addEventListener("storage", (e) => {
      if (e.key === STORAGE_KEY) refreshUI();
    });
    window.addEventListener(CHANGE_EVENT, () => refreshUI());

    // Optional MutationObserver (kept as originally)
    if (document.body) {
      const observer = new MutationObserver(() => {});
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  document.addEventListener("DOMContentLoaded", init);
  window.ShopifyWishlist = Wishlist;
})();